import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type User } from "@/lib/api";

export interface RegisterData {
  email: string;
  password: string;
  accepted_terms: boolean;
}

interface AuthState {
  user: User | null | false;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
  adopt: (u: User) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);

  useEffect(() => {
    if (window.location.hash.includes("session_id=")) return;
    api.get<User>("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<User>("/auth/login", { email, password });
    setUser(data);
  }, []);

  const register = useCallback(async (payload: RegisterData) => {
    const { data } = await api.post<User>("/auth/register", payload);
    setUser(data);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get<User>("/auth/me");
    setUser(data);
  }, []);

  const clear = useCallback(() => setUser(false), []);
  const adopt = useCallback((u: User) => setUser(u), []);

  return <AuthContext.Provider value={{ user, login, register, logout, refresh, clear, adopt }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
